'use client';

import { isHeidiSupportReachable } from '@/utils/heidiReach';
import { formatMessageOutsideReact } from '@heidi/runtime/i18n';
import i18next from 'i18next';
import { defineMessages } from 'react-intl';

const messages = defineMessages({
  unknownErrorContactIt: {
    defaultMessage:
      'We hit an unexpected error while signing in. Try again, and contact your IT team if the issue persists.',
    id: 'wH2a3pxLik',
  },
});

export type DesktopAuthErrorCode =
  | 'login_required'
  | 'consent_required'
  | 'mfa_required'
  | 'invalid_grant'
  | 'invalid_client'
  | 'access_denied'
  | 'rate_limited'
  | 'token_exchange_failed'
  | 'network_error'
  | 'secure_storage_unavailable'
  | 'browser_launch_failed'
  | 'attempt_expired'
  | 'state_mismatch'
  | 'callback_replay'
  | 'no_login_attempt'
  | 'unknown_error';

export type DesktopAuthErrorAction = 'retry' | 'back-to-sign-in' | 'support';

export type DesktopAuthErrorContent = {
  code: DesktopAuthErrorCode;
  title: string;
  description: string;
  action: DesktopAuthErrorAction;
  helperText?: string;
};

export type DesktopAuthErrorEventPayload = {
  code?: string;
  description?: string | null;
  correlation_id?: string | null;
  attempt_id?: string | null;
};

type DesktopAuthErrorConfig = {
  titleKey: string;
  descriptionKey: string;
  helperTextKey?: string;
  action: DesktopAuthErrorAction;
};

const ERROR_MAP: Record<DesktopAuthErrorCode, DesktopAuthErrorConfig> = {
  login_required: {
    titleKey: 'desktopAuth.errors.login_required.title',
    descriptionKey: 'desktopAuth.errors.login_required.description',
    action: 'retry',
  },
  consent_required: {
    titleKey: 'desktopAuth.errors.consent_required.title',
    descriptionKey: 'desktopAuth.errors.consent_required.description',
    action: 'back-to-sign-in',
  },
  mfa_required: {
    titleKey: 'desktopAuth.errors.mfa_required.title',
    descriptionKey: 'desktopAuth.errors.mfa_required.description',
    action: 'retry',
  },
  invalid_grant: {
    titleKey: 'desktopAuth.errors.invalid_grant.title',
    descriptionKey: 'desktopAuth.errors.invalid_grant.description',
    action: 'back-to-sign-in',
  },
  invalid_client: {
    titleKey: 'desktopAuth.errors.invalid_client.title',
    descriptionKey: 'desktopAuth.errors.invalid_client.description',
    helperTextKey: 'desktopAuth.errors.invalid_client.helperText',
    action: 'support',
  },
  access_denied: {
    titleKey: 'desktopAuth.errors.access_denied.title',
    descriptionKey: 'desktopAuth.errors.access_denied.description',
    action: 'back-to-sign-in',
  },
  rate_limited: {
    titleKey: 'desktopAuth.errors.rate_limited.title',
    descriptionKey: 'desktopAuth.errors.rate_limited.description',
    action: 'back-to-sign-in',
  },
  token_exchange_failed: {
    titleKey: 'desktopAuth.errors.token_exchange_failed.title',
    descriptionKey: 'desktopAuth.errors.token_exchange_failed.description',
    action: 'retry',
  },
  network_error: {
    titleKey: 'desktopAuth.errors.network_error.title',
    descriptionKey: 'desktopAuth.errors.network_error.description',
    action: 'retry',
  },
  secure_storage_unavailable: {
    titleKey: 'desktopAuth.errors.secure_storage_unavailable.title',
    descriptionKey: 'desktopAuth.errors.secure_storage_unavailable.description',
    action: 'support',
  },
  browser_launch_failed: {
    titleKey: 'desktopAuth.errors.browser_launch_failed.title',
    descriptionKey: 'desktopAuth.errors.browser_launch_failed.description',
    action: 'retry',
  },
  attempt_expired: {
    titleKey: 'desktopAuth.errors.attempt_expired.title',
    descriptionKey: 'desktopAuth.errors.attempt_expired.description',
    action: 'back-to-sign-in',
  },
  state_mismatch: {
    titleKey: 'desktopAuth.errors.state_mismatch.title',
    descriptionKey: 'desktopAuth.errors.state_mismatch.description',
    action: 'back-to-sign-in',
  },
  callback_replay: {
    titleKey: 'desktopAuth.errors.callback_replay.title',
    descriptionKey: 'desktopAuth.errors.callback_replay.description',
    action: 'back-to-sign-in',
  },
  no_login_attempt: {
    titleKey: 'desktopAuth.errors.no_login_attempt.title',
    descriptionKey: 'desktopAuth.errors.no_login_attempt.description',
    action: 'back-to-sign-in',
  },
  unknown_error: {
    titleKey: 'desktopAuth.errors.unknown_error.title',
    descriptionKey: 'desktopAuth.errors.unknown_error.description',
    action: 'retry',
  },
};

export function getDesktopAuthError(code?: string): DesktopAuthErrorContent {
  const normalized = (code ?? 'unknown_error').toLowerCase() as DesktopAuthErrorCode;

  const errorConfig = ERROR_MAP[normalized] ?? ERROR_MAP.unknown_error;

  const title = i18next.t(errorConfig.titleKey);
  const description =
    !isHeidiSupportReachable && errorConfig === ERROR_MAP.unknown_error
      ? formatMessageOutsideReact(messages.unknownErrorContactIt)
      : i18next.t(errorConfig.descriptionKey);
  const helperText = errorConfig.helperTextKey
    ? i18next.t(errorConfig.helperTextKey)
    : undefined;

  return {
    code: normalized,
    title,
    description,
    action: errorConfig.action,
    ...(helperText && { helperText }),
  };
}
